import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * One filter bar per report screen, not one per tab.
 *
 * The bar holds the controls; the line underneath holds the filters that produced the figures
 * currently on screen. Those are different facts, and keeping them apart is what stops the reader
 * mistaking a filter they have edited but not run for the one the numbers came from.
 */

export interface ReportFilterFieldProps {
  label: string;
  children: React.ReactNode;
  /** One short line under the control. Not a place for a paragraph. */
  hint?: string;
  htmlFor?: string;
  className?: string;
}

export function ReportFilterField({
  label,
  children,
  hint,
  htmlFor,
  className,
}: ReportFilterFieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** One active-filter chip. `accent` marks the period, which every report is scoped by. */
export interface ReportFilterChip {
  key: string;
  label: string;
  tone?: 'default' | 'accent';
  /** Renders a remove affordance. Omit for a chip that cannot be cleared (the period). */
  onRemove?: () => void;
}

export interface ReportFilterBarProps {
  children: React.ReactNode;
  /** Pinned to the end of the bar: the Run button and the export controls. */
  actions?: React.ReactNode;
  /** The filters the figures on screen were produced with. Empty hides the line. */
  chips?: ReportFilterChip[];
  className?: string;
  testId?: string;
}

export function ReportFilterBar({
  children,
  actions,
  chips = [],
  className,
  testId,
}: ReportFilterBarProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        {children}
        {actions && <div className="flex flex-wrap items-center gap-2 ms-auto self-end">{actions}</div>}
      </div>

      {chips.length > 0 && (
        <div
          className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3"
          role="group"
          aria-label={t('reports.activeFilters')}
          data-testid="report-active-filters"
        >
          {chips.map((chip) => (
            <span
              key={chip.key}
              data-testid={`report-filter-chip-${chip.key}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs',
                chip.tone === 'accent'
                  ? 'border-primary/40 bg-primary/10 text-primary font-medium'
                  : 'border-border bg-muted text-muted-foreground-strong',
              )}
            >
              {chip.label}
              {chip.onRemove && (
                <button
                  type="button"
                  onClick={chip.onRemove}
                  aria-label={t('reports.removeFilter', { filter: chip.label })}
                  className="rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReportFilterBar;
